const STORAGE_KEY = "orbithub:idle-session";
const ACTIVITY_THROTTLE_MS = 1000;
const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

interface IdleSession {
  token: string;
  idleTimeoutMs: number;
  lastActivityAt: number;
  ended?: boolean;
}

interface IdleMonitorOptions {
  getToken: () => string;
  getPath: () => string;
  onTimeout: () => void;
  onSessionEnded: () => void;
  onSessionChanged: () => void;
}

let memorySession: IdleSession | undefined;

const readSession = (): IdleSession | undefined => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (
      value &&
      typeof value.token === "string" &&
      Number.isFinite(value.idleTimeoutMs) &&
      value.idleTimeoutMs > 0 &&
      Number.isFinite(value.lastActivityAt)
    ) {
      return value;
    }
    return undefined;
  } catch {
    return memorySession;
  }
};

const writeSession = (session: IdleSession) => {
  memorySession = { ...session };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    console.warn("[IdleSession] Cross-tab storage unavailable", error);
  }
};

// const logActivity = (session: IdleSession) => {
//   console.info("[IdleSession] lastActivityAt", {
//     lastActivityAt: session.lastActivityAt,
//     lastActivityTime: new Date(session.lastActivityAt).toISOString(),
//     expiresAt: session.lastActivityAt + session.idleTimeoutMs,
//     idleTimeoutMs: session.idleTimeoutMs,
//   });
// };

export const initializeIdleSession = (token: string, idleTimeoutMs?: number) => {
  const validTimeout = Number.isFinite(idleTimeoutMs) && Number(idleTimeoutMs) > 0;
  if (!validTimeout) {
    console.warn("[IdleSession] Missing login idleTimeoutMs; using 120 minutes");
  }
  const session = {
    token,
    idleTimeoutMs: validTimeout ? Number(idleTimeoutMs) : DEFAULT_IDLE_TIMEOUT_MS,
    lastActivityAt: Date.now(),
  };
  writeSession(session);
  // logActivity(session);
};

export const endIdleSession = (token: string) => {
  const session = readSession();
  if (session?.token === token && !session.ended) {
    writeSession({ ...session, ended: true });
  }
};

export const isIdleSessionEnded = (token: string): boolean => {
  const session = readSession();
  return !!token && session?.token === token && session.ended === true;
};

export const createIdleMonitor = (options: IdleMonitorOptions) => {
  let session: IdleSession | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let publishTimer: ReturnType<typeof setTimeout> | undefined;
  let lastPublishedAt = 0;
  let exited = false;
  let stopped = false;
  const events = ["pointerdown", "keydown", "wheel", "touchmove"] as const;
  const exempt = () => ["/login", "/data-screen"].includes(options.getPath());

  const clearTimers = () => {
    clearTimeout(timer);
    clearTimeout(publishTimer);
    timer = undefined;
    publishTimer = undefined;
  };

  const exit = (timeout: boolean) => {
    if (exited || stopped) return;
    exited = true;
    clearTimers();
    if (timeout) options.onTimeout();
    else options.onSessionEnded();
  };

  const mergeSharedSession = (): boolean => {
    const token = options.getToken();
    if (!token) {
      session = undefined;
      exited = false;
      clearTimers();
      return false;
    }
    if (session?.token !== token) {
      session = undefined;
      exited = false;
    }
    const shared = readSession();
    if (shared && shared.token !== token) {
      clearTimers();
      if (!exited) {
        exited = true;
        options.onSessionChanged();
      }
      return false;
    }
    if (!shared) {
      // Legacy logins have no reliable activity timestamp. Require login again.
      if (!exempt()) exit(true);
      return false;
    }
    if (shared.ended) {
      exit(false);
      return false;
    }
    session = {
      ...shared,
      lastActivityAt: Math.max(shared.lastActivityAt, session?.lastActivityAt || 0),
    };
    return !exited;
  };

  const publish = () => {
    publishTimer = undefined;
    if (stopped || !mergeSharedSession() || !session) return;
    writeSession(session);
    lastPublishedAt = Date.now();
    // logActivity(session);
  };

  const sync = (countAsActivity = false) => {
    clearTimeout(timer);
    timer = undefined;
    if (stopped || !mergeSharedSession() || !session || exempt()) return;
    const now = Date.now();
    // Check before accepting a refresh/click, so an expired session cannot revive.
    if (now >= session.lastActivityAt + session.idleTimeoutMs) {
      exit(true);
      return;
    }
    if (countAsActivity) {
      session.lastActivityAt = now;
      publish();
    }
    if (exited || !session) return;
    timer = setTimeout(
      () => sync(),
      Math.min(session.lastActivityAt + session.idleTimeoutMs - now, 2147483647),
    );
  };

  const onActivity = (event: Event) => {
    if (
      stopped || exited || !event.isTrusted ||
      document.visibilityState !== "visible" || exempt() || !options.getToken()
    ) return;
    const now = Date.now();
    if (!session || now >= session.lastActivityAt + session.idleTimeoutMs) sync();
    if (!session || exited) return;
    session.lastActivityAt = now;
    // Only memory changes per event. Storage and console writes are throttled.
    if (now - lastPublishedAt >= ACTIVITY_THROTTLE_MS) {
      clearTimeout(publishTimer);
      publish();
    } else if (!publishTimer) {
      publishTimer = setTimeout(publish, ACTIVITY_THROTTLE_MS - (now - lastPublishedAt));
    }
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) {
      // Preserve a newer timestamp even if another tab's concurrent write was older.
      if (event.newValue && session) {
        try {
          const incoming = JSON.parse(event.newValue);
          if (incoming.token === session.token && Number.isFinite(incoming.lastActivityAt)) {
            session.lastActivityAt = Math.max(session.lastActivityAt, incoming.lastActivityAt);
          }
        } catch {
          // sync() validates the current storage value.
        }
      }
      sync();
    }
    if (event.key === "token" && options.getToken()) {
      const currentToken = localStorage.getItem("token");
      if (!currentToken) exit(false);
      else if (currentToken !== options.getToken()) options.onSessionChanged();
    }
  };
  const onResume = () => {
    if (document.visibilityState === "visible") sync();
  };
  const onPageHide = () => {
    if (publishTimer) {
      clearTimeout(publishTimer);
      publish();
    }
  };

  events.forEach((name) => document.addEventListener(name, onActivity, { capture: true, passive: true }));
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", onResume);
  window.addEventListener("pageshow", onResume);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onResume);

  return {
    sync,
    stop: () => {
      stopped = true;
      clearTimers();
      events.forEach((name) => document.removeEventListener(name, onActivity, true));
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onResume);
      window.removeEventListener("pageshow", onResume);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onResume);
    },
  };
};
